import { ComponentType, ReactNode, Suspense, useEffect, useRef, useState } from "react";
import React from "react";
interface LazyLoadSectionProps {
  id: string;
  component: ComponentType<any>;
  fallback: ReactNode;
  threshold?: number;
  rootMargin?: string;
}

/**
 * A component that lazy loads a component only when scrolled into view
 * This helps improve initial page load performance by deferring the rendering
 * of sections until they are about to be visible to the user
 */
const LazyLoadSection = ({
  id,
  component: Component,
  fallback,
  threshold = 0.1,
  rootMargin = "200px 0px"
}: LazyLoadSectionProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  // Handle client-side only mounting to prevent hydration issues
  useEffect(() => {
    setIsMounted(true);
  }, []);
  useEffect(() => {
    // Skip if not mounted yet (server-side or initial render)
    // Also skip if already visible
    if (!isMounted || isVisible) return;

    // Capture the current value of the ref inside the effect
    const currentSectionRef = sectionRef.current;
    if (!currentSectionRef) return; // Element not rendered yet

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        // Once the component is loaded, we don't need to observe it anymore
        observer.unobserve(currentSectionRef);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(currentSectionRef);
    return () => {
      // Use the captured value in the cleanup function
      observer.unobserve(currentSectionRef);
    };
    // Rerun if threshold/margin changes, but only after mount
  }, [isMounted, isVisible, threshold, rootMargin]);

  // Error boundary fallback
  const handleError = () => {
    console.error(`Error loading component in section ${id}`);
    setHasError(true);
  };

  // Show fallback if there was an error
  if (hasError) {
    return <div id={id} className="py-8">
                <div className="container mx-auto px-4 text-center">
                    <p className="text-red-400 mb-2">Failed to load section</p>
                    <button onClick={() => {
          setHasError(false);
          // Reset visibility briefly to allow re-triggering if needed
          setIsVisible(false);
          setTimeout(() => {
            // Attempt to set visible again after a short delay
            // This might re-trigger intersection observer if element is still in view
            const currentRef = sectionRef.current;
            if (currentRef) {
              const observer = new IntersectionObserver(([entry]) => {
                if (entry.isIntersecting) {
                  setIsVisible(true);
                  observer.unobserve(currentRef);
                }
              }, {
                threshold,
                rootMargin
              });
              observer.observe(currentRef);
            }
          }, 100);
        }} className="px-4 py-2 bg-slate-700 rounded-md hover:bg-slate-600 text-sm">
                        Try again
                    </button>
                </div>
            </div>;
  }

  // Determine props for the container div.
  // Only add the ref on the client after mount AND before it's visible
  // so the IntersectionObserver can attach.
  const divProps: React.HTMLAttributes<HTMLDivElement> & {
    ref?: React.Ref<HTMLDivElement>;
  } = {
    id
  };
  if (isMounted && !isVisible) {
    divProps.ref = sectionRef;
  }
  return <div {...divProps} data-sentry-component="LazyLoadSection" data-sentry-source-file="LazyLoadSection.tsx">
            {isMounted && isVisible ?
    // Only render Suspense/Component once mounted AND visible
    <Suspense fallback={fallback}>
                    <ErrorBoundary onError={handleError}>
                        <Component />
                    </ErrorBoundary>
                </Suspense> :
    // Render fallback during SSR, initial client render,
    // and while mounted but not yet visible.
    fallback}
        </div>;
};

// Simple error boundary component
class ErrorBoundary extends React.Component<{
  children: ReactNode;
  onError: () => void;
}> {
  componentDidCatch(error: Error) {
    console.error("Component error:", error);
    this.props.onError();
  }
  render() {
    return this.props.children;
  }
}
export default LazyLoadSection;